"use client"

import { useState } from "react"
import { usePathname } from "next/navigation"
import { motion } from "framer-motion"
import { Section } from "@/components/layout/Section"
import { Button } from "@/components/ui/button"
import { 
  Briefcase, 
  MapPin, 
  Clock, 
  Users, 
  Code, 
  Palette, 
  Smartphone, 
  Zap,
  ArrowRight,
  CheckCircle2,
  Heart,
  TrendingUp,
  Coffee
} from "lucide-react"
import Link from "next/link"
import { CTA } from "@/components/sections/CTA"
import { type Locale } from "@/lib/i18n"

const jobOpenings = [
  {
    id: "1",
    title: "Senior Frontend Developer",
    department: "Web Geliştirme",
    location: "İstanbul / Uzaktan",
    type: "Tam Zamanlı",
    icon: Code,
    description: "Next.js, React ve TypeScript ile modern web uygulamaları geliştirecek deneyimli frontend developer arıyoruz.",
    requirements: [
      "5+ yıl React/Next.js deneyimi",
      "TypeScript ve modern JavaScript bilgisi",
      "Tailwind CSS ve responsive design",
      "Git ve versiyon kontrolü",
      "Agile/Scrum metodolojisi deneyimi"
    ],
    benefits: [
      "Rekabetçi maaş",
      "Esnek çalışma saatleri",
      "Uzaktan çalışma imkanı",
      "Sağlık sigortası",
      "Eğitim ve gelişim desteği"
    ]
  },
  {
    id: "2",
    title: "UI/UX Designer",
    department: "Tasarım",
    location: "İstanbul / Hibrit",
    type: "Tam Zamanlı",
    icon: Palette,
    description: "Kullanıcı deneyimi odaklı, modern ve şık tasarımlar yapacak yaratıcı UI/UX tasarımcı arıyoruz.",
    requirements: [
      "3+ yıl UI/UX tasarım deneyimi",
      "Figma, Adobe XD veya Sketch",
      "Prototipleme ve wireframing",
      "Kullanıcı araştırması deneyimi",
      "Portfolio gereklidir"
    ],
    benefits: [
      "Rekabetçi maaş",
      "Esnek çalışma saatleri",
      "Hibrit çalışma modeli",
      "Sağlık sigortası",
      "Tasarım araçları lisansı"
    ]
  },
  {
    id: "3",
    title: "Mobile App Developer",
    department: "Mobil Geliştirme",
    location: "İstanbul / Uzaktan",
    type: "Tam Zamanlı",
    icon: Smartphone,
    description: "React Native veya Flutter ile iOS ve Android uygulamaları geliştirecek mobil uygulama geliştirici arıyoruz.",
    requirements: [
      "3+ yıl mobil uygulama geliştirme",
      "React Native veya Flutter",
      "iOS/Android native geliştirme",
      "API entegrasyonu deneyimi",
      "App Store ve Play Store yayınlama"
    ],
    benefits: [
      "Rekabetçi maaş",
      "Esnek çalışma saatleri",
      "Uzaktan çalışma imkanı",
      "Sağlık sigortası",
      "Yeni teknolojiler öğrenme desteği"
    ]
  },
  {
    id: "4",
    title: "AI/ML Engineer",
    department: "AI & Otomasyon",
    location: "İstanbul / Uzaktan",
    type: "Tam Zamanlı",
    icon: Zap,
    description: "Yapay zeka ve makine öğrenmesi teknolojileri ile çözümler geliştirecek AI/ML mühendisi arıyoruz.",
    requirements: [
      "2+ yıl AI/ML deneyimi",
      "Python ve ML framework'leri",
      "LLM ve AI API'leri deneyimi",
      "Veri analizi ve işleme",
      "AI ürünleştirme deneyimi"
    ],
    benefits: [
      "Rekabetçi maaş",
      "Esnek çalışma saatleri",
      "Uzaktan çalışma imkanı",
      "Sağlık sigortası",
      "AI konferans ve eğitim desteği"
    ]
  },
]

const benefits = [
  { icon: TrendingUp, title: "Kariyer Gelişimi", description: "Sürekli öğrenme ve gelişim fırsatları" },
  { icon: Heart, title: "Sağlık & Refah", description: "Kapsamlı sağlık sigortası ve refah paketi" },
  { icon: Coffee, title: "Esnek Çalışma", description: "Uzaktan ve hibrit çalışma seçenekleri" },
  { icon: Users, title: "Takım Kültürü", description: "Destekleyici ve işbirlikçi ekip ortamı" },
]

export default function CareerPage() {
  const pathname = usePathname()
  const currentLocale: Locale = pathname?.startsWith('/en/') ? 'en' : 'tr'
  const [selectedJob, setSelectedJob] = useState<string | null>(null)

  return (
    <>
      {/* Hero Section */}
      <Section className="relative overflow-hidden bg-gradient-to-br from-[rgb(3,74,166)] via-[rgb(3,74,166)] to-[rgb(2,40,89)] text-white pt-24 pb-16 sm:pt-32 sm:pb-24">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_70%)]" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex p-4 bg-white/10 backdrop-blur-sm rounded-2xl mb-8"
          >
            <Briefcase className="h-12 w-12 sm:h-16 sm:w-16" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 font-mono"
          >
            Kariyer
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto"
          >
            Dijital dönüşüm yolculuğunda bize katılın. Modern teknolojilerle çalışın, sürekli öğrenin ve kariyerinizi bir sonraki seviyeye taşıyın.
          </motion.p>
        </div>
      </Section>

      {/* Why Join Us Section */}
      <Section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-6 font-mono">
              Neden LAK VERSE?
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Modern teknolojiler, yaratıcı projeler ve destekleyici bir ekip ortamı. Kariyerinizi bizimle birlikte şekillendirin.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-all duration-300"
                >
                  <div className="inline-flex p-3 bg-[rgb(3,74,166)]/10 rounded-lg mb-4">
                    <Icon className="h-6 w-6 text-[rgb(3,74,166)]" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2 font-mono">{benefit.title}</h3>
                  <p className="text-slate-600 text-sm">{benefit.description}</p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </Section>

      {/* Job Openings Section (temporarily without listings) */}
      <Section className="py-16 lg:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-6 font-mono">
              Açık Pozisyonlar
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Şu anda aktif bir ilanımız bulunmuyor.
            </p>
            <p className="text-sm text-slate-500 mt-2 max-w-2xl mx-auto">
              Yine de ekibimize katılmak isterseniz, aşağıdaki genel başvuru formunu doldurabilirsiniz.
            </p>
          </div>

          {/* General Application */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-gradient-to-br from-[rgb(3,74,166)] to-[rgb(2,40,89)] rounded-xl p-8 text-white text-center"
          >
            <h3 className="text-2xl font-bold mb-4 font-mono">Genel Başvuru</h3>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              Deneyiminize ve ilgi alanlarınıza uygun bir pozisyon açıldığında sizinle iletişime geçebilmemiz için genel başvuru formunu doldurun.
            </p>
            <Button
              asChild
              size="lg"
              className="bg-white text-[rgb(3,74,166)] hover:bg-slate-100"
            >
              <Link href="/kariyer/basvuru" className="flex items-center gap-2">
                Genel Başvuru Yap
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </Section>
      <CTA locale={currentLocale} />
    </>
  )
}

